// EvalManagerPage.js

import React, { useContext, useState, useRef, useLayoutEffect } from 'react';
import { Box, Card, CardBody, Image, ResponsiveContext, Heading, Text, Grid } from 'grommet';
import { useNavigate } from "react-router-dom";
import { UserContext } from "../App";
import HomePageNav from '../components/Base components/HomePageNav';
import { Cluster, Group, DocumentTest, Blank } from 'grommet-icons';
import Notification from "../components/Base components/Notification";

// ... (CultivarIcon and CardItem components remain the same)
const CultivarIcon = (props) => ( <Blank {...props}><svg viewBox="0 0 1800 1800" xmlns="http://www.w3.org/2000/svg"><path fill="currentColor" d="M16 38q27 117 77.5 184.5T215 314t165.5 28.5T589 339q61-3 93-4Q629-106 16 38zm271 366q-104 0-178 74.5T35 657q0 103 74.5 177.5T287 909q60 0 112-26t87-72q-37-71-37-154q0-84 37-155q-35-46-87-72t-112-26zm554 6q3-104 69.5-175T1074 133t218-33V0q-86 1-164 17T980.5 65.5t-120.5 80t-81 115T749 409q-90 14-149.5 84.5T540 657q0 104 74 178t178 74t178-74t74-178q0-91-58-160.5T841 410zm455-6q-60 0-112 26t-87 72q37 71 37 155q0 83-37 154q35 46 87 72t112 26q104 0 178-74t74-178t-74-178.5t-178-74.5zm-23 594q-139-9-229-109q-92 100-230 109q-22 48-22 105q0 104 74 178t178 74t178-74t74-178q0-54-23-105zm-535 260q-37-72-37-155q0-57 19-112q-109-23-180-102q-92 100-230 109q-23 58-23 105q0 104 74.5 178t178.5 74q59 0 111-26t87-71zm283 187q-138-11-229-111q-92 100-231 111q-21 50-21 103q0 104 74 178t178 74t178-74t74-178q0-55-23-103z"/></svg></Blank> );
const CardItem = ({ title, description, icon, onClick, headingLevel }) => ( <Card background="light-1" hoverIndicator onClick={onClick} focusIndicator={false} round="medium" elevation="medium"><CardBody pad="medium" gap="medium" align="start">{icon}<Box><Heading level={headingLevel} margin="none">{title}</Heading><Text color="text-weak">{description}</Text></Box></CardBody></Card> );


const EvalManagerPage = () => {
    const navigate = useNavigate();
    const currentUser = useContext(UserContext);
    const size = useContext(ResponsiveContext);

    const [headerHeight, setHeaderHeight] = useState(0);
    const headerRef = useRef(null);

    useLayoutEffect(() => {
        if (headerRef.current) {
            setHeaderHeight(headerRef.current.offsetHeight);
        }
    }, [size]);

    const [showError, setShowError] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState("");

    const handleCardClick = (path, adminOnly = false) => {
        if (!currentUser) { setNotificationMessage("Please log in to access this feature."); setShowError(true); return; }
        if (adminOnly) { if (currentUser.isAdmin) { navigate(path); } else { setNotificationMessage("You do not have permission to access this page."); setShowError(true); } } else { navigate(path); }
    };
    
    const managementItems = [
        { title: "Vineyards Management", description: "Manage fields.", IconComponent: Cluster, onClick: () => handleCardClick("/fields-manager", true) },
        { title: "Cultivars Management", description: "View and manage grape cultivars.", IconComponent: CultivarIcon, onClick: () => handleCardClick("/cult-manager", true) },
        { title: "Treatment Templates", description: "Define default treatment plans.", IconComponent: DocumentTest, onClick: () => handleCardClick("/st-treatment", true) },
        { title: "User Management", description: "Add, edit, and manage system users.", IconComponent: Group, onClick: () => handleCardClick("/user-manager", true) },
    ];

    let gridColumns, cardIconSize, cardHeadingLevel, gridMaxWidth, gridGap;
    switch (size) {
        case 'xlarge': case 'large': gridColumns = { count: 2, size: 'auto' }; cardIconSize = 'xlarge'; cardHeadingLevel = 2; gridMaxWidth = '1200px'; gridGap = 'xlarge'; break;
        case 'medium': gridColumns = { count: 2, size: 'auto' }; cardIconSize = 'large'; cardHeadingLevel = 3; gridMaxWidth = '1000px'; gridGap = 'large'; break;
        default: gridColumns = ['auto']; cardIconSize = 'large'; cardHeadingLevel = 3; gridMaxWidth = '100%'; gridGap = 'medium'; break;
    }

    return (
        <Box width="90vw" height="100vh" background="light-2" margin="0 auto" >
            
            <Box
                ref={headerRef}
                style={{ 
                    position: 'fixed',
                    width: 'inherit',
                    top: 0,
                    zIndex: 10,
                }}
            > 
                <HomePageNav isDefault={true} />
                
                <Box
                    background="neutral-3"
                    pad={{vertical: "xxsmall"}}
                    margin={{vertical: "xxsmall"}}
                    style={{ position: 'relative' }}
                    align="center"
                    justify="center"
                >
                    <Text size="large" weight="bold" color="#ffffff" > Evaluation management </Text>
                    <Box 
                        width={size === "small" || size === "xsmall" ? "70px" : size === "medium" ? "90px" : "120px"}                                     
                        height={size === "small" || size === "xsmall" ? "70px" : size === "medium" ? "90px" : "120px"}                      
                        style={{
                            position:"absolute", 
                            right: "3%", 
                            top: "150%", // Move the top of the element to the middle
                            transform: 'translateY(-50%)' // Pull the element up by half its own height
                        }}
                    >
                        <Image fit="contain" src="Grape.png" />
                    </Box>                         
                </Box>
            </Box>
            
            <Box
                fill
                style={{
                    marginTop: `${headerHeight}px`, // pushes below header
                }}
            >
           
                <Box
                    overflow="auto"
                    margin='auto'
                    pad={{ vertical: 'large', horizontal: 'medium' }} // Adds padding INSIDE the box
                >
                    <Grid
                        columns={gridColumns}
                        gap={gridGap}
                        width={{ max: gridMaxWidth }}
                    >
                        {managementItems.map(item => {
                            const Icon = item.IconComponent;
                            const iconElement = <Icon size={cardIconSize} color="brand" />;
                            return ( <CardItem key={item.title} title={item.title} description={item.description} icon={iconElement} onClick={item.onClick} headingLevel={cardHeadingLevel}/> );
                        })}
                    </Grid>
                </Box>
            
            </Box>

            {showError && (<Notification status="critical" message={`${notificationMessage}`} onClose={() => setShowError(false)}/> )}
        </Box>
    );
};

export default EvalManagerPage;
